/*
DATA EXAMPLES (do not delete):

{
  "success": true,
  "data": {
    "message": "Simulation results",
    "start_date": "2020-01-01",
    "end_date": "2024-01-01",
    "trading_dates": 1006,
    "snapshots": [],
    "performance_summary": {
      "initial_value": 100000,
      "final_value": 74608.5234375,
      "total_return_pct": -25.391477584838867,
      "total_benchmark_return_pct": -33.70553606881738,
      "total_alpha_pct": 8.31405848397851,
      "annualized_return_pct": -25.47849464416504,
      "annualized_benchmark_return_pct": -33.81401794609993,
      "annualized_alpha_pct": 8.348528020884661,
      "annualized_volatility_pct": 35.7854118731518,
      "sharpe_ratio": -0.7583829466055194,
      "benchmark_annualized_volatility_pct": 32.76901392724467,
      "benchmark_sharpe_ratio": -1.2221326269866466,
      "risk_free_rate": 0.04,
      "days_simulated": 251,
      "total_trades": 418,
      "final_positions": 40,
      "final_cash_pct": 4.99595308303833
    },
    "scenario_name": "compare_2020-01-01_4years",
    "scenario_config": {
      "name": "compare_2020-01-01_4years",
      "start_date": "2020-01-01",
      "end_date": "2024-01-01",
      "rebalance_frequency_days": 21
    }
  }
}
  

*/

import { useMemo } from 'react';
import { MESSAGES } from './constants';

interface EventData {
  message: string;
  data?: {
    performance_summary?: {
      initial_value: number;
      final_value: number;
      total_return_pct: number;
      total_alpha_pct: number;
      annualized_return_pct: number;
    };
  };
}

interface ResultsSummaryProps {
  data?: EventData[];
  progress?: number;
  connectionStatus?: 'disconnected' | 'connecting' | 'connected' | 'completed' | 'error';
}

/**
 * ResultsSummary Component
 *
 * Displays a summary of portfolio results and metrics from Simulation results events.
 *
 * @param {Object} props
 * @param {Array} props.data - Array of relevant events from WebSocket
 */
const ResultsSummary: React.FC<ResultsSummaryProps> = ({ data = [], progress, connectionStatus }) => {
  // Extract simulation results
  const summaryData = useMemo(() => {
    const simulationEvent = data.find(
      (event) =>
        event.message === MESSAGES.SIMULATION_RESULTS &&
        event.data?.performance_summary
    );

    if (!simulationEvent || !simulationEvent.data?.performance_summary) {
      return null;
    }

    const perf = simulationEvent.data.performance_summary;
    return {
      initialValue: perf.initial_value,
      finalValue: perf.final_value,
      totalReturnPct: perf.total_return_pct,
      totalAlphaPct: perf.total_alpha_pct,
      averageYearReturnPct: perf.annualized_return_pct,
    };
  }, [data]);

  const METRIC_LABELS = ['Initial Value', 'Final Value', 'Total Return', 'Year Return', 'Alpha'];
  const progressLabel =
    connectionStatus === 'completed'
      ? 'Finalizing results...'
      : progress !== undefined && progress > 0
      ? `${Math.round(progress)}% through simulation — results arrive at the end`
      : 'Results arrive when the simulation finishes';

  if (!summaryData) {
    return (
      <div>
        <h3 className="mb-5 font-display text-foreground">
          Results Summary
        </h3>
        <div
          className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-5 p-5 bg-white/3 border border-white/8 rounded-lg"
          aria-hidden
        >
          {METRIC_LABELS.map((label) => (
            <div key={label}>
              <div className="mb-2.5 text-[13px] font-semibold text-muted-foreground">
                {label}
              </div>
              <div className="animate-pulse rounded bg-muted" style={{ height: '26px', width: '70%' }} />
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-muted-foreground text-center">
          {progressLabel}
        </p>
      </div>
    );
  }

  return (
    <div>
      <h3 className="mb-5 font-display text-foreground">Results Summary</h3>
      <div
        className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-5 p-5 bg-white/3 backdrop-blur-[10px] border border-white/10 rounded-lg"
      >
        <div>
          <strong className="block mb-2 text-foreground">
            Initial Value
          </strong>
          <span className="text-xl font-bold text-primary">
            $
            {summaryData.initialValue.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </div>
        <div>
          <strong className="block mb-2 text-foreground">
            Final Value
          </strong>
          <span className="text-xl font-bold text-positive">
            $
            {summaryData.finalValue.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </div>
        <div>
          <strong className="block mb-2 text-foreground">
            Total Return
          </strong>
          <span className="text-xl font-bold text-positive">
            {summaryData.totalReturnPct.toFixed(2)}%
          </span>
        </div>
        <div>
          <strong className="block mb-2 text-foreground">
            Average Year Return
          </strong>
          <span className="text-xl font-bold text-positive">
            {summaryData.averageYearReturnPct
              ? summaryData.averageYearReturnPct.toFixed(2)
              : 'N/A'}
            %
          </span>
        </div>
        <div>
          <strong className="block mb-2 text-foreground">
            Alpha (performance vs index)
          </strong>
          <span className="text-xl font-bold text-positive">
            {summaryData.totalAlphaPct.toFixed(2)}%
          </span>
        </div>
      </div>
    </div>
  );
};

export default ResultsSummary;
